import { classesApi } from "@/modules/classes/presentation/api/client";
import { classSessionsKeys } from "@/modules/classes/presentation/api/queries";
import { privateLessonsApi } from "@/modules/private-lessons/presentation/api/client";
import { privateLessonBookingsKeys } from "@/modules/private-lessons/presentation/api/queries";
import { roomBookingsKeys } from "@/modules/room-bookings/presentation/api/queries";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    bulkCheckInClassSession,
    bulkUndoCheckInClassSession,
    bulkUndoCheckInPrivateBooking,
    checkInClassSession,
    checkInInstructorClassSession,
    checkInInstructorPrivateBooking,
    checkInPrivateBooking,
    completeRoomBooking,
    getAttendanceRecords,
    getCheckInList,
    getInstructorCheckInRecords,
    guestInstructorCheckInClassSession,
    guestInstructorCheckInPrivateBooking,
    patchAttendanceRecordTime,
    patchInstructorCheckInTime,
    substituteInstructorCheckInClassSession,
    substituteInstructorCheckInPrivateBooking,
    undoCheckInClassSession,
    undoCheckInInstructorClassSession,
    undoCheckInInstructorPrivateBooking,
    undoCheckInPrivateBooking,
} from "./client";

export const attendanceKeys = {
  all: ["attendance"] as const,
  checkInList: (date: string) =>
    [...attendanceKeys.all, "check-in-list", date] as const,
  records: (query: unknown) =>
    [...attendanceKeys.all, "records", query] as const,
  instructorRecords: (query: unknown) =>
    [...attendanceKeys.all, "instructor-records", query] as const,
};

export function useCheckInListQuery(date: string) {
  return useQuery({
    queryKey: attendanceKeys.checkInList(date),
    queryFn: () => getCheckInList(date),
    enabled: !!date,
  });
}

export function useBookingInstructorAttendeesQuery(bookingId: string) {
  return useQuery({
    queryKey: [
      "private-lessons",
      "bookings",
      "instructor-attendees",
      bookingId,
    ],
    queryFn: () => privateLessonsApi.bookings.getInstructorAttendees(bookingId),
    enabled: !!bookingId,
  });
}

export function useCheckInClassSessionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      studentId,
      overrideReason,
    }: {
      sessionId: string;
      studentId: string;
      overrideReason?: string;
    }) => checkInClassSession(sessionId, { studentId, overrideReason }),
    onSuccess: (_, { sessionId }) => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
      qc.invalidateQueries({ queryKey: classSessionsKeys.detail(sessionId) });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ກວດຊື່ຄລາດສ໌ບໍ່ສຳເລັດ");
    },
  });
}

export function useBulkCheckInClassSessionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      studentIds,
      instructorIds,
    }: {
      sessionId: string;
      studentIds: string[];
      instructorIds?: string[];
    }) => bulkCheckInClassSession(sessionId, { studentIds, instructorIds }),
    onSuccess: (_, { sessionId }) => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
      qc.invalidateQueries({ queryKey: classSessionsKeys.detail(sessionId) });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ກວດຊື່ບໍ່ສຳເລັດ");
    },
  });
}

export function useCheckInPrivateBookingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookingId,
      overrideReason,
      instructorIds,
      memberStudentIds,
    }: {
      bookingId: string;
      overrideReason?: string;
      instructorIds?: string[];
      memberStudentIds?: string[];
    }) =>
      checkInPrivateBooking(bookingId, {
        overrideReason,
        instructorIds,
        memberStudentIds,
      }),
    onSuccess: (_, { bookingId }) => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
      qc.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          q.queryKey[0] === "private-lessons" &&
          (q.queryKey[1] === "bookings" || q.queryKey.includes(bookingId)),
      });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ກວດຊື່ຮຽນສ່ວນຕົວບໍ່ສຳເລັດ");
    },
  });
}

export function useCheckInInstructorClassSessionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      instructorId,
    }: {
      sessionId: string;
      instructorId: string;
    }) => checkInInstructorClassSession(sessionId, { instructorId }),
    onSuccess: (_, { sessionId }) => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
      qc.invalidateQueries({ queryKey: classSessionsKeys.detail(sessionId) });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ກວດຊື່ຄູສອນບໍ່ສຳເລັດ");
    },
  });
}

export function useCheckInInstructorPrivateBookingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookingId,
      instructorId,
    }: {
      bookingId: string;
      instructorId: string;
    }) => checkInInstructorPrivateBooking(bookingId, { instructorId }),
    onSuccess: (_, { bookingId }) => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
      qc.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          q.queryKey[0] === "private-lessons" &&
          q.queryKey[1] === "bookings" &&
          q.queryKey[2] === "instructor-attendees",
      });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ກວດຊື່ຄູສອນບໍ່ສຳເລັດ");
    },
  });
}

export function useSubstituteInstructorCheckInClassSessionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      instructorId,
      substituteForInstructorId,
      substituteReason,
    }: {
      sessionId: string;
      instructorId: string;
      substituteForInstructorId?: string | null;
      substituteReason?: string | null;
    }) =>
      substituteInstructorCheckInClassSession(sessionId, {
        instructorId,
        substituteForInstructorId,
        substituteReason,
      }),
    onSuccess: (_, { sessionId }) => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
      qc.invalidateQueries({ queryKey: classSessionsKeys.detail(sessionId) });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ກວດຊື່ຄູສອນສອນແທນບໍ່ສຳເລັດ");
    },
  });
}

export function useGuestInstructorCheckInClassSessionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      name,
      phoneNumber,
      email,
      substituteForInstructorId,
      substituteReason,
      ratePerSession,
      payoutBasis,
    }: {
      sessionId: string;
      name: string;
      phoneNumber?: string | null;
      email?: string | null;
      substituteForInstructorId?: string | null;
      substituteReason?: string | null;
      ratePerSession?: number | null;
      payoutBasis?: "per_hour" | "per_session";
    }) =>
      guestInstructorCheckInClassSession(sessionId, {
        name,
        phoneNumber,
        email,
        substituteForInstructorId,
        substituteReason,
        ratePerSession,
        payoutBasis,
      }),
    onSuccess: (_, { sessionId }) => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
      qc.invalidateQueries({ queryKey: classSessionsKeys.detail(sessionId) });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ກວດຊື່ຄູສອນຊົ່ວຄາວບໍ່ສຳເລັດ");
    },
  });
}

export function useSubstituteInstructorCheckInPrivateBookingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookingId,
      instructorId,
      substituteForInstructorId,
      substituteReason,
    }: {
      bookingId: string;
      instructorId: string;
      substituteForInstructorId?: string | null;
      substituteReason?: string | null;
    }) =>
      substituteInstructorCheckInPrivateBooking(bookingId, {
        instructorId,
        substituteForInstructorId,
        substituteReason,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
      qc.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          q.queryKey[0] === "private-lessons" &&
          q.queryKey[1] === "bookings" &&
          q.queryKey[2] === "instructor-attendees",
      });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ກວດຊື່ຄູສອນສອນແທນບໍ່ສຳເລັດ");
    },
  });
}

export function useGuestInstructorCheckInPrivateBookingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookingId,
      name,
      phoneNumber,
      email,
      substituteForInstructorId,
      substituteReason,
      ratePerSession,
      payoutBasis,
    }: {
      bookingId: string;
      name: string;
      phoneNumber?: string | null;
      email?: string | null;
      substituteForInstructorId?: string | null;
      substituteReason?: string | null;
      ratePerSession?: number | null;
      payoutBasis?: "per_hour" | "per_session";
    }) =>
      guestInstructorCheckInPrivateBooking(bookingId, {
        name,
        phoneNumber,
        email,
        substituteForInstructorId,
        substituteReason,
        ratePerSession,
        payoutBasis,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
      qc.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          q.queryKey[0] === "private-lessons" &&
          q.queryKey[1] === "bookings" &&
          q.queryKey[2] === "instructor-attendees",
      });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ກວດຊື່ຄູສອນຊົ່ວຄາວບໍ່ສຳເລັດ");
    },
  });
}

export function useCompleteClassSessionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => classesApi.sessions.complete(sessionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
      qc.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "classes" && query.queryKey[1] === "sessions",
      });
      toast.success("ສຳເລັດ ຊົ່ວໂມງຮຽນ ຄລາດສ໌ສຳເລັດ");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ສຳເລັດ ຊົ່ວໂມງຮຽນ ຄລາດສ໌ບໍ່ສຳເລັດ");
    },
  });
}

export function useAttendanceRecordsQuery(query: {
  limit?: number;
  offset?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  filters?: Array<{ field: string; op: string; value: unknown }>;
}) {
  return useQuery({
    queryKey: attendanceKeys.records(query),
    queryFn: () => getAttendanceRecords(query),
  });
}

export function useUndoCheckInClassSessionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      attendanceId,
    }: {
      sessionId: string;
      attendanceId: string;
    }) => undoCheckInClassSession(sessionId, { attendanceId }),
    onSuccess: (_, { sessionId }) => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
      qc.invalidateQueries({ queryKey: classSessionsKeys.detail(sessionId) });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ຍົກເລີກການກວດຊື່ບໍ່ສຳເລັດ");
    },
  });
}

export function useUndoCheckInInstructorClassSessionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      instructorCheckInId,
    }: {
      sessionId: string;
      instructorCheckInId: string;
    }) =>
      undoCheckInInstructorClassSession(sessionId, {
        instructorCheckInId,
      }),
    onSuccess: (_, { sessionId }) => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
      qc.invalidateQueries({ queryKey: classSessionsKeys.detail(sessionId) });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ຍົກເລີກການກວດຊື່ຄູສອນບໍ່ສຳເລັດ");
    },
  });
}

export function useUndoCheckInPrivateBookingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookingId,
      attendanceId,
    }: {
      bookingId: string;
      attendanceId: string;
    }) => undoCheckInPrivateBooking(bookingId, { attendanceId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ຍົກເລີກການກວດຊື່ຮຽນສ່ວນຕົວບໍ່ສຳເລັດ");
    },
  });
}

export function useUndoCheckInInstructorPrivateBookingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookingId,
      instructorCheckInId,
    }: {
      bookingId: string;
      instructorCheckInId: string;
    }) =>
      undoCheckInInstructorPrivateBooking(bookingId, {
        instructorCheckInId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ຍົກເລີກການກວດຊື່ຄູສອນບໍ່ສຳເລັດ");
    },
  });
}

export function useBulkUndoCheckInClassSessionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      attendanceIds,
      instructorCheckInIds,
    }: {
      sessionId: string;
      attendanceIds: string[];
      instructorCheckInIds?: string[];
    }) =>
      bulkUndoCheckInClassSession(sessionId, {
        attendanceIds,
        instructorCheckInIds,
      }),
    onSuccess: (_, { sessionId }) => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
      qc.invalidateQueries({ queryKey: classSessionsKeys.detail(sessionId) });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ຍົກເລີກການກວດຊື່ບໍ່ສຳເລັດ");
    },
  });
}

export function useBulkUndoCheckInPrivateBookingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookingId,
      attendanceId,
      instructorCheckInIds,
    }: {
      bookingId: string;
      attendanceId?: string;
      instructorCheckInIds?: string[];
    }) =>
      bulkUndoCheckInPrivateBooking(bookingId, {
        attendanceId,
        instructorCheckInIds,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ຍົກເລີກການກວດຊື່ບໍ່ສຳເລັດ");
    },
  });
}

export function useInstructorCheckInRecordsQuery(query: {
  limit?: number;
  offset?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  filters?: Array<{ field: string; op: string; value: unknown }>;
}) {
  return useQuery({
    queryKey: attendanceKeys.instructorRecords(query),
    queryFn: () => getInstructorCheckInRecords(query),
  });
}

export function useCompleteRoomBookingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bookingId: string) => completeRoomBooking(bookingId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
      toast.success("ອັບເດດການຈອງຫ້ອງເປັນສຳເລັດແລ້ວ");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "ອັບເດດການຈອງຫ້ອງບໍ່ສຳເລັດ");
    },
  });
}

function mapCheckInTimeError(error: Error): string {
  const msg = error.message ?? "";
  if (msg.includes("payroll") || msg.includes("PAYROLL")) {
    return "ປິດງວດ payroll ແລ້ວ — ບໍ່ສາມາດແກ້ເວລາ check-in ໄດ້";
  }
  if (msg === "Forbidden") {
    return "ບໍ່ມີສິດແກ້ເວລາ check-in";
  }
  if (msg.toLowerCase().includes("cancelled")) {
    return "ບໍ່ສາມາດແກ້ເວລາ check-in ຂອງ session ທີ່ຖືກຍົກເລີກ";
  }
  return msg || "ອັບເດດເວລາ check-in ບໍ່ສຳເລັດ";
}

export function usePatchInstructorCheckInTimeMutation(options?: {
  onSuccess?: () => void;
}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      checkInId,
      checkedInAt,
    }: {
      checkInId: string;
      checkedInAt: string;
    }) => patchInstructorCheckInTime(checkInId, { checkedInAt }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
      qc.invalidateQueries({ queryKey: classSessionsKeys.all });
      qc.invalidateQueries({ queryKey: privateLessonBookingsKeys.all });
      qc.invalidateQueries({ queryKey: roomBookingsKeys.all });
      toast.success("ອັບເດດເວລາ check-in ສຳເລັດ");
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(mapCheckInTimeError(error));
    },
  });
}

export function usePatchAttendanceRecordTimeMutation(options?: {
  onSuccess?: () => void;
}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      recordId,
      checkedInAt,
    }: {
      recordId: string;
      checkedInAt: string;
    }) => patchAttendanceRecordTime(recordId, { checkedInAt }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attendanceKeys.all });
      qc.invalidateQueries({ queryKey: classSessionsKeys.all });
      qc.invalidateQueries({ queryKey: privateLessonBookingsKeys.all });
      qc.invalidateQueries({
        queryKey: ["private-lessons", "bookings", "attendance"],
      });
      toast.success("ອັບເດດເວລາ check-in ສຳເລັດ");
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(mapCheckInTimeError(error));
    },
  });
}
